import React, { useMemo } from "react";
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import {
  Building2, Wallet, TrendingUp, CheckCircle2, Clock, CalendarClock, Download,
  Mail, Phone, Globe, PiggyBank, Percent, Hourglass, Sheet,
} from "lucide-react";
import { alpha, mix, readableOn, themeOf, safeAccent } from "@/lib/portalTheme";

const usd = (n, dec = 2) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: dec, maximumFractionDigits: dec })}`;
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const monLabel = (m) => {
  const [y, mm] = String(m || "").split("-");
  return MON[+mm - 1] ? `${MON[+mm - 1]} ${String(y).slice(2)}` : m;
};
const PAID = "#00863A";
const dayLabel = (d) => {
  const [y, m, dd] = String(d || "").split("-");
  return MON[+m - 1] ? `${+dd} ${MON[+m - 1]} ${y}` : d;
};
const LABEL = "text-[10.5px] font-bold uppercase tracking-[0.14em]";

const card = (t) => ({ background: t.card, border: `1px solid ${t.border}`, borderRadius: 20 });

const Stat = ({ label, value, sub, Icon, t, color, tid }) => (
  <div className="p-4 md:p-5" style={card(t)} data-testid={tid}>
    <div className={`${LABEL} flex items-center gap-1.5`} style={{ color: t.ink3 }}>
      <Icon size={12} /> {label}
    </div>
    <div className="font-display text-xl md:text-[26px] font-semibold tracking-tight mt-1.5"
      style={{ color: color || t.ink }}>{value}</div>
    {sub && <div className="text-[11.5px] mt-1" style={{ color: t.ink3 }}>{sub}</div>}
  </div>
);

const Bare = ({ t, children, tid, className = "" }) => (
  <div className={`p-5 md:p-6 ${className}`} style={card(t)} data-testid={tid}>{children}</div>
);

/* Value over time: capital placed with the return stacked on top of it. */
const GrowthChart = ({ rows, t, accent, tid }) => (
  <div style={{ height: 230 }} data-testid={tid}>
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={rows} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id="capFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={accent} stopOpacity={0.26} />
            <stop offset="100%" stopColor={accent} stopOpacity={0.04} />
          </linearGradient>
          <linearGradient id="earnFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={PAID} stopOpacity={0.5} />
            <stop offset="100%" stopColor={PAID} stopOpacity={0.1} />
          </linearGradient>
        </defs>
        <CartesianGrid stroke={t.border} vertical={false} />
        <XAxis dataKey="month" tickFormatter={monLabel} stroke={t.ink3} fontSize={11} tickLine={false} axisLine={false} minTickGap={18} />
        <YAxis stroke={t.ink3} fontSize={11} tickLine={false} axisLine={false}
          tickFormatter={(v) => `$${Number(v).toLocaleString(undefined, { notation: "compact" })}`} />
        <Tooltip
          contentStyle={{ background: t.cardStrong, border: `1px solid ${t.border}`, borderRadius: 12, color: t.ink, fontSize: 12 }}
          labelFormatter={monLabel} formatter={(v, n) => [usd(v), n === "capital" ? "Capital placed" : "Return earned"]} />
        <Area type="monotone" dataKey="capital" stackId="v" stroke={accent} strokeWidth={2} fill="url(#capFill)" />
        <Area type="monotone" dataKey="earned" stackId="v" stroke={PAID} strokeWidth={2} fill="url(#earnFill)" />
      </AreaChart>
    </ResponsiveContainer>
  </div>
);

/* Where this is heading if the agreed rate holds — an estimate, labelled as one. */
const OutlookChart = ({ rows, t, accent, tid }) => (
  <div style={{ height: 210 }} data-testid={tid}>
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={rows} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
        <CartesianGrid stroke={t.border} vertical={false} />
        <XAxis dataKey="month" tickFormatter={monLabel} stroke={t.ink3} fontSize={11} tickLine={false} axisLine={false} minTickGap={18} />
        <YAxis stroke={t.ink3} fontSize={11} tickLine={false} axisLine={false}
          tickFormatter={(v) => `$${Number(v).toLocaleString(undefined, { notation: "compact" })}`} />
        <Tooltip
          contentStyle={{ background: t.cardStrong, border: `1px solid ${t.border}`, borderRadius: 12, color: t.ink, fontSize: 12 }}
          labelFormatter={monLabel}
          formatter={(v, n) => [usd(v), n === "earned" ? "Earned so far" : "Projected"]} />
        <Line type="monotone" dataKey="earned" stroke={PAID} strokeWidth={2.4} dot={false} connectNulls />
        <Line type="monotone" dataKey="projected" stroke={accent} strokeWidth={2.2} dot={false}
          strokeDasharray="5 4" connectNulls />
      </LineChart>
    </ResponsiveContainer>
  </div>
);

/* What each month earned, coloured by whether it's been paid yet. */
const MonthlyChart = ({ rows, t, accent, tid }) => (
  <div style={{ height: 230 }} data-testid={tid}>
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={rows} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
        <CartesianGrid stroke={t.border} vertical={false} />
        <XAxis dataKey="month" tickFormatter={monLabel} stroke={t.ink3} fontSize={11} tickLine={false} axisLine={false} minTickGap={12} />
        <YAxis stroke={t.ink3} fontSize={11} tickLine={false} axisLine={false}
          tickFormatter={(v) => `$${Number(v).toLocaleString(undefined, { notation: "compact" })}`} />
        <Tooltip cursor={{ fill: alpha(accent, 0.06) }}
          contentStyle={{ background: t.cardStrong, border: `1px solid ${t.border}`, borderRadius: 12, color: t.ink, fontSize: 12 }}
          labelFormatter={monLabel}
          formatter={(v, n, p) => [usd(v), p?.payload?.state === "paid" ? "Paid to you" : p?.payload?.state === "due" ? "Due to you" : "Still running"]} />
        <Bar dataKey="earned" radius={[6, 6, 0, 0]} maxBarSize={38}>
          {rows.map((r) => (
            <Cell key={r.month} fill={r.state === "paid" ? PAID : r.state === "due" ? accent : alpha(accent, 0.34)} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  </div>
);

const CommitmentCard = ({ c, t, accent }) => {
  const elapsed = (c.months || []).length;
  const term = Number(c.term_months) || 0;
  const pct = term ? Math.min(100, Math.round((elapsed / term) * 100)) : 0;
  const paidPct = c.accrued > 0 ? Math.min(100, Math.round((c.paid / c.accrued) * 100)) : 0;
  const expectedMonthly = (c.capital || 0) * (Number(c.promised_rate_pct) || 0) / 100;
  const live = elapsed > 0;
  return (
    <div className="p-5 md:p-6" style={card(t)} data-testid={`statement-commitment-${c.reference}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className={LABEL} style={{ color: t.ink3 }}>Capital placed</div>
          <div className="font-display text-[26px] font-semibold tracking-tight" style={{ color: t.ink }}>{usd(c.capital, 0)}</div>
          <div className="flex items-center gap-2 mt-2 flex-wrap text-[11.5px]" style={{ color: t.ink2 }}>
            {c.reference && (
              <span className="font-mono px-2 py-0.5 rounded-lg" style={{ background: alpha(accent, 0.12), color: accent }}
                data-testid={`statement-ref-${c.reference}`}>{c.reference}</span>
            )}
            <span>{c.promised_rate_pct}% a month · {c.term_months} months</span>
            <span>· {c.payout_mode === "monthly" ? "paid monthly" : "paid at maturity"}</span>
          </div>
        </div>
        <div className="text-right">
          <div className={LABEL} style={{ color: t.ink3 }}>{live ? "Return accrued" : "Expected each month"}</div>
          <div className="font-display text-[22px] font-semibold tracking-tight" style={{ color: live ? PAID : t.ink }}>
            {usd(live ? c.accrued : expectedMonthly)}
          </div>
          {live && c.maturity ? (
            <div className="text-[11.5px] mt-1 inline-flex items-center gap-1.5" style={{ color: t.ink3 }}>
              <CalendarClock size={12} /> matures {c.maturity}
            </div>
          ) : (
            <span className="text-[11px] font-semibold px-2 py-0.5 rounded-lg mt-1 inline-block"
              style={{ background: t.band, color: t.ink3 }} data-testid={`statement-status-${c.reference}`}>
              {String(c.status || "").replace(/_/g, " ") || "not started"}
            </span>
          )}
        </div>
      </div>

      {live ? (
        <div className="grid sm:grid-cols-2 gap-4 mt-5">
          {[["Term", `${elapsed} of ${term || "—"} months`, pct, accent],
            ["Paid out", `${usd(c.paid)} of ${usd(c.accrued)}`, paidPct, PAID]].map(([k, v, p, col]) => (
            <div key={k}>
              <div className="flex items-center justify-between text-[11.5px] mb-1.5">
                <span style={{ color: t.ink3 }}>{k}</span>
                <span style={{ color: t.ink2 }}>{v}</span>
              </div>
              <div className="h-2 rounded-full overflow-hidden" style={{ background: t.band }}>
                <div className="h-full rounded-full transition-all" style={{ width: `${p}%`, background: col }} />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="mt-5 rounded-2xl px-4 py-3.5 text-[12.5px]" style={{ background: t.band, color: t.ink2 }}
          data-testid={`statement-not-started-${c.reference}`}>
          Nothing has accrued on this one yet. Once your adviser confirms the funds, it earns about{" "}
          <b style={{ color: t.ink }}>{usd(expectedMonthly)}</b> a month at the rate you agreed — about{" "}
          <b style={{ color: t.ink }}>{usd(expectedMonthly * term, 0)}</b> over the {term} months.
        </div>
      )}

      {live && (
        <table className="w-full text-sm mt-5">
          <thead>
            <tr className={LABEL} style={{ color: t.ink3 }}>
              <th className="text-left py-1.5 font-bold">Month</th>
              <th className="text-left font-bold">Return earned</th>
              <th className="text-right font-bold">Status</th>
            </tr>
          </thead>
          <tbody>
            {[...c.months].reverse().map((m) => (
              <tr key={m.month} style={{ borderTop: `1px solid ${t.border}` }} data-testid={`statement-month-${m.month}`}>
                <td className="py-2" style={{ color: t.ink2 }}>{monLabel(m.month)}</td>
                <td className="font-semibold" style={{ color: t.ink }}>{usd(m.earned)}</td>
                <td className="text-right">
                  <span className="text-[11px] font-semibold px-2 py-0.5 rounded-lg"
                    style={m.paid
                      ? { background: alpha(PAID, 0.14), color: PAID }
                      : m.booked ? { background: alpha(accent, 0.14), color: accent } : { background: t.band, color: t.ink3 }}>
                    {m.paid ? "paid" : m.booked ? "due to you" : "still running"}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

const PaymentsCard = ({ payments, t, accent }) => {
  if (!payments?.length) return null;
  const total = payments.reduce((a, p) => a + (p.amount || 0), 0);
  return (
    <Bare t={t} tid="statement-payments">
      <div className="flex items-end justify-between gap-4 flex-wrap mb-3">
        <div>
          <div className={LABEL} style={{ color: t.ink3 }}>Payments to you</div>
          <div className="font-display text-lg font-semibold tracking-tight" style={{ color: t.ink }}>
            {usd(total)} settled across {payments.length} payment{payments.length === 1 ? "" : "s"}
          </div>
        </div>
        <div className="text-[11.5px] inline-flex items-center gap-1.5" style={{ color: t.ink3 }}>
          <CheckCircle2 size={13} style={{ color: PAID }} /> confirmed by your adviser
        </div>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className={LABEL} style={{ color: t.ink3 }}>
            <th className="text-left py-1.5 font-bold">Paid on</th>
            <th className="text-left font-bold">Covering</th>
            <th className="text-right font-bold">Amount</th>
          </tr>
        </thead>
        <tbody>
          {payments.map((p) => (
            <tr key={p.paid_on} style={{ borderTop: `1px solid ${t.border}` }} data-testid={`statement-payment-${p.paid_on}`}>
              <td className="py-2.5 whitespace-nowrap" style={{ color: t.ink2 }}>{dayLabel(p.paid_on)}</td>
              <td className="text-[12.5px]" style={{ color: t.ink3 }}>
                {p.months.length > 2
                  ? `${monLabel(p.months[0])} – ${monLabel(p.months[p.months.length - 1])} · ${p.months.length} months`
                  : p.months.map(monLabel).join(" · ")}
              </td>
              <td className="text-right font-semibold whitespace-nowrap" style={{ color: PAID }}>{usd(p.amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Bare>
  );
};

const AdviserCard = ({ b, t, accent }) => {
  const rows = [[Mail, b.contact_email, `mailto:${b.contact_email}`], [Phone, b.contact_phone, `tel:${String(b.contact_phone || "").replace(/[^\d+]/g, "")}`], [Globe, b.website, b.website]];
  if (!b.adviser_name && !b.contact_email && !b.contact_phone) return null;
  return (
    <Bare t={t} tid="statement-adviser">
      <div className="flex items-start gap-4 flex-wrap">
        {b.adviser_photo
          ? <img src={b.adviser_photo} alt="" className="w-14 h-14 rounded-2xl object-cover" />
          : <span className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: alpha(accent, 0.13), color: accent }}><Building2 size={20} /></span>}
        <div className="min-w-0 flex-1">
          <div className={LABEL} style={{ color: t.ink3 }}>Your adviser</div>
          <div className="font-display text-lg font-semibold tracking-tight" style={{ color: t.ink }}>{b.adviser_name || b.name}</div>
          {b.adviser_title && <div className="text-[12px]" style={{ color: t.ink3 }}>{b.adviser_title}</div>}
          <div className="flex flex-wrap gap-x-5 gap-y-1.5 mt-3 text-[12.5px]">
            {rows.filter(([, v]) => v).map(([Icon, v, href]) => (
              <a key={v} href={href} target={href?.startsWith("http") ? "_blank" : undefined} rel="noreferrer"
                className="inline-flex items-center gap-2 hover:underline" style={{ color: t.ink2 }}>
                <Icon size={13} style={{ color: accent }} /> {v}
              </a>
            ))}
          </div>
        </div>
      </div>
    </Bare>
  );
};

/** A client's statement, in their adviser's colours — tiles, charts, every month.
 *  Shared by the private link (/s/:token) and the signed-in portal. */
export const StatementView = ({ d, actions, subtitle, onDownload, banner }) => {
  const b = d.brand || {};
  const accent = safeAccent(b.accent || "#4060FF", b.theme);
  const t = themeOf(b.theme);
  const onAccent = readableOn(accent);
  const payable = d.totals.payable ?? d.totals.outstanding;
  const accruing = d.totals.accruing || 0;
  const heldToMaturity = d.totals.at_maturity || 0;
  const commitments = useMemo(() => d.commitments || [], [d.commitments]);

  const monthly = useMemo(() => {
    const by = {};
    commitments.forEach((c) => (c.months || []).forEach((m) => {
      const x = (by[m.month] = by[m.month] || { month: m.month, earned: 0, paid: 0, due: 0, running: 0 });
      x.earned += m.earned || 0;
      if (m.paid) x.paid += m.earned || 0;
      else if (m.booked) x.due += m.earned || 0;
      else x.running += m.earned || 0;
    }));
    return Object.values(by).sort((x, y) => x.month.localeCompare(y.month)).map((x) => ({
      ...x,
      earned: +x.earned.toFixed(2),
      state: x.running > x.paid + x.due ? "running" : x.due >= x.paid ? "due" : "paid",
    }));
  }, [commitments]);

  const growth = useMemo(() => {
    let run = 0;
    return monthly.map((m) => {
      run += m.earned;
      const capital = commitments
        .filter((c) => String(c.started_on || "").slice(0, 7) <= m.month)
        .reduce((a, c) => a + (c.capital || 0), 0);
      return { month: m.month, capital: +capital.toFixed(2), earned: +run.toFixed(2) };
    });
  }, [monthly, commitments]);

  const blendedRate = useMemo(() => {
    const cap = commitments.reduce((a, c) => a + (c.capital || 0), 0);
    if (!cap) return null;
    return commitments.reduce((a, c) => a + (c.capital || 0) * (Number(c.promised_rate_pct) || 0), 0) / cap;
  }, [commitments]);
  const nextMaturity = useMemo(
    () => commitments.map((c) => c.maturity).filter(Boolean).sort()[0] || null, [commitments]);
  const monthsRunning = monthly.length;
  const bestMonth = monthly.length ? Math.max(...monthly.map((m) => m.earned)) : 0;
  const returnPct = d.totals.capital > 0 ? (d.totals.accrued / d.totals.capital) * 100 : 0;
  const awaitingCapital = commitments
    .filter((c) => !(c.months || []).length)
    .reduce((a, c) => a + (c.capital || 0), 0);
  const expectedMonthly = commitments.reduce(
    (a, c) => a + (c.capital || 0) * (Number(c.promised_rate_pct) || 0) / 100, 0);

  // Where this is heading if the agreed rate holds. Each commitment earns until
  // its own maturity, so the line steps DOWN as plans finish — no straight-line
  // fantasy, and nothing here is a promise.
  const outlook = useMemo(() => {
    const running = commitments.filter((c) => (c.capital || 0) > 0);
    if (!running.length) return { rows: [], byMaturity: 0, horizon: null, months: 0 };
    const addMonths = (ym, n) => {
      const [y, m] = ym.split("-").map(Number);
      const t0 = (y * 12 + (m - 1)) + n;
      return `${Math.floor(t0 / 12)}-${String((t0 % 12) + 1).padStart(2, "0")}`;
    };
    const last = monthly.length ? monthly[monthly.length - 1].month
      : new Date().toISOString().slice(0, 7);
    const ends = running.map((c) => String(c.maturity || "").slice(0, 7)).filter(Boolean).sort();
    const horizon = ends.length ? ends[ends.length - 1] : addMonths(last, 12);
    let steps = 0;
    while (addMonths(last, steps + 1) <= horizon && steps < 36) steps += 1;
    if (!steps) return { rows: [], byMaturity: 0, horizon: null, months: 0 };

    const history = monthly.map((m, i) => ({
      month: m.month,
      earned: +monthly.slice(0, i + 1).reduce((x, y) => x + y.earned, 0).toFixed(2),
      projected: null,
    }));
    let run = d.totals.accrued || 0;
    const future = [];
    for (let i = 1; i <= steps; i += 1) {
      const month = addMonths(last, i);
      run += running.reduce((acc, c) => {
        const end = String(c.maturity || "").slice(0, 7);
        if (end && month > end) return acc;
        return acc + (c.capital || 0) * (Number(c.promised_rate_pct) || 0) / 100;
      }, 0);
      future.push({ month, earned: null, projected: +run.toFixed(2) });
    }
    // join the two lines so there's no gap at today
    if (history.length) history[history.length - 1].projected = history[history.length - 1].earned;
    return { rows: [...history, ...future], byMaturity: +run.toFixed(2), horizon, months: steps };
  }, [commitments, monthly, d.totals.accrued]);

  const downloadCsv = () => {
    const rows = [[`${b.name || "Statement"} — ${d.client.name} (${d.client.reference})`],
      [], ["Month", "Earned", "Paid to you", "Due to you", "Still running"]];
    monthly.forEach((m) => rows.push([m.month, m.earned.toFixed(2), m.paid.toFixed(2),
      m.due.toFixed(2), m.running.toFixed(2)]));
    rows.push([], ["Commitment", "Plan", "Capital", "Rate %/mo", "Started", "Maturity", "Accrued", "Paid"]);
    commitments.forEach((c) => rows.push([c.reference, c.offer_name || "", c.capital,
      c.promised_rate_pct, c.started_on || "", c.maturity || "", c.accrued, c.paid]));
    rows.push([], ["Your capital", d.totals.capital], ["Return accrued", d.totals.accrued],
      ["Paid to you", d.totals.paid], ["Ready to be paid", payable]);
    const blob = new Blob([rows.map((r) => r.map((v) => `"${String(v ?? "")}"`).join(",")).join("\n")],
      { type: "text/csv;charset=utf-8" });
    const el = document.createElement("a");
    el.href = URL.createObjectURL(blob);
    el.download = `statement-${d.client.reference || "mine"}-${new Date().toISOString().slice(0, 10)}.csv`;
    el.click();
    URL.revokeObjectURL(el.href);
  };

  return (
    <div className="min-h-screen pb-20" style={{ background: t.pageBg, color: t.ink }} data-testid="client-statement-page">
      <div className="px-6 md:px-10 pt-8 pb-24"
        style={{ background: `linear-gradient(115deg, ${mix(accent, "#000000", 0.3)} 0%, ${accent} 62%, ${mix(accent, "#FFFFFF", 0.16)} 100%)` }}>
        <div className="max-w-[1080px] mx-auto flex items-center gap-3 flex-wrap">
          {b.logo
            ? <img src={b.logo} alt="" style={{ height: 34, objectFit: "contain" }} />
            : <span className="w-10 h-10 rounded-2xl flex items-center justify-center" style={{ background: alpha(onAccent === "#FFFFFF" ? "#FFFFFF" : "#0F1226", 0.22) }}><Building2 size={18} color={onAccent} /></span>}
          <div className="min-w-0">
            <div className="font-display text-2xl font-semibold" style={{ color: onAccent }} data-testid="statement-brand">{b.name}</div>
            <div className="text-sm" style={{ color: alpha(onAccent === "#FFFFFF" ? "#FFFFFF" : "#0F1226", 0.86) }}>
              {subtitle || `Statement for ${d.client.name} · ${d.client.reference}`}
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <button type="button" onClick={downloadCsv}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-[12.5px] font-semibold transition-transform hover:-translate-y-0.5"
              style={{ background: alpha(onAccent === "#FFFFFF" ? "#FFFFFF" : "#0F1226", 0.2), color: onAccent }}
              data-testid="statement-download-csv">
              <Sheet size={13} /> CSV
            </button>
            {onDownload && (
              <button type="button" onClick={onDownload}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-[12.5px] font-semibold transition-transform hover:-translate-y-0.5"
                style={{ background: alpha(onAccent === "#FFFFFF" ? "#FFFFFF" : "#0F1226", 0.2), color: onAccent }}
                data-testid="statement-download-pdf">
                <Download size={13} /> PDF
              </button>
            )}
            {actions}
          </div>
        </div>
      </div>

      <div className="max-w-[1080px] mx-auto px-6 md:px-10 -mt-16 space-y-5 relative">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Stat t={t} Icon={Wallet} label="Your capital" value={usd(d.totals.capital, 0)}
            sub={awaitingCapital > 0
              ? `${usd(awaitingCapital, 0)} awaiting funds`
              : `${commitments.length || 0} commitment${commitments.length === 1 ? "" : "s"}`}
            tid="statement-kpi-your-capital" />
          <Stat t={t} Icon={TrendingUp} label="Return accrued" value={usd(d.totals.accrued)} color={PAID}
            sub={returnPct ? `${returnPct.toFixed(1)}% of your capital` : (expectedMonthly > 0 ? `${usd(expectedMonthly)} a month once funded` : null)} tid="statement-kpi-return-accrued" />
          <Stat t={t} Icon={CheckCircle2} label="Paid to you" value={usd(d.totals.paid)}
            sub={monthsRunning ? `over ${monthsRunning} month${monthsRunning === 1 ? "" : "s"}` : null} tid="statement-kpi-paid-to-you" />
          <Stat t={t} Icon={Clock} label="Ready to be paid" value={usd(payable)} color={payable > 0 ? accent : t.ink}
            sub={heldToMaturity > 0
              ? `${usd(heldToMaturity)} held to your maturity date`
              : (accruing > 0 ? `+ ${usd(accruing)} still running this month` : null)}
            tid="statement-kpi-ready-to-be-paid" />
        </div>

        {banner}

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Stat t={t} Icon={Percent} label="Your rate" value={blendedRate != null ? `${blendedRate.toFixed(2)}%` : "—"} sub="a month, as agreed" tid="statement-kpi-rate" />
          <Stat t={t} Icon={PiggyBank} label="Best month" value={usd(bestMonth)} sub="most earned in one month" tid="statement-kpi-best-month" />
          <Stat t={t} Icon={Hourglass} label="Months running" value={monthsRunning || "—"} sub="since your first month" tid="statement-kpi-months" />
          <Stat t={t} Icon={CalendarClock} label="Next maturity" value={nextMaturity || "—"} sub={nextMaturity ? "first commitment to mature" : "nothing maturing yet"} tid="statement-kpi-maturity" />
        </div>

        {!commitments.length && (
          <Bare t={t} tid="statement-empty" className="text-center">
            <div className="font-display text-lg font-semibold" style={{ color: t.ink }}>Nothing is running yet</div>
            <p className="text-sm mt-1.5" style={{ color: t.ink2 }}>
              Once your funds are confirmed, your return starts accruing and every month shows up here.
            </p>
          </Bare>
        )}

        {!!commitments.length && !monthly.length && (
          <Bare t={t} tid="statement-what-next">
            <div className={LABEL} style={{ color: t.ink3 }}>What happens next</div>
            <div className="font-display text-lg font-semibold tracking-tight mt-1" style={{ color: t.ink }}>
              Your place is held — nothing has started earning yet
            </div>
            <div className="grid sm:grid-cols-3 gap-px rounded-2xl overflow-hidden mt-4" style={{ background: t.border }}>
              {[["Funds confirmed", `${b.name} confirms the transfer with you and marks it received.`],
                ["Return starts", `From that day it earns about ${usd(expectedMonthly)} a month at the rate you agreed.`],
                ["Statement each month", "Every month lands here and in your inbox as a one-page PDF."]].map(([k, v], i) => (
                <div key={k} className="px-5 py-4" style={{ background: t.card }}>
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="w-6 h-6 rounded-lg flex items-center justify-center text-[11px] font-bold"
                      style={{ background: alpha(accent, 0.13), color: accent }}>{i + 1}</span>
                    <span className="text-[13px] font-semibold" style={{ color: t.ink }}>{k}</span>
                  </div>
                  <p className="text-[12px] leading-relaxed" style={{ color: t.ink2 }}>{v}</p>
                </div>
              ))}
            </div>
          </Bare>
        )}

        {growth.length > 1 && (
          <div className="grid lg:grid-cols-2 gap-3">
            <Bare t={t} tid="statement-growth-card">
              <div className={LABEL} style={{ color: t.ink3 }}>Your position over time</div>
              <div className="font-display text-lg font-semibold tracking-tight mb-3" style={{ color: t.ink }}>
                {usd(d.totals.capital + d.totals.accrued, 0)} capital and return
              </div>
              <GrowthChart rows={growth} t={t} accent={accent} tid="statement-growth-chart" />
              <div className="flex items-center gap-4 mt-3 text-[11.5px]" style={{ color: t.ink3 }}>
                {[["capital placed", accent], ["return earned", PAID]].map(([k, col]) => (
                  <span key={k} className="inline-flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-sm" style={{ background: col }} /> {k}
                  </span>
                ))}
              </div>
            </Bare>
            <Bare t={t} tid="statement-monthly-card">
              <div className={LABEL} style={{ color: t.ink3 }}>Month by month</div>
              <div className="font-display text-lg font-semibold tracking-tight mb-3" style={{ color: t.ink }}>
                What each month earned you
              </div>
              <MonthlyChart rows={monthly} t={t} accent={accent} tid="statement-monthly-chart" />
              <div className="flex items-center gap-4 mt-3 text-[11.5px]" style={{ color: t.ink3 }}>
                {[["paid", PAID], ["due to you", accent], ["still running", alpha(accent, 0.34)]].map(([k, col]) => (
                  <span key={k} className="inline-flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-sm" style={{ background: col }} /> {k}
                  </span>
                ))}
              </div>
            </Bare>
          </div>
        )}

        {outlook.rows.length > 1 && (
          <Bare t={t} tid="statement-outlook-card">
            <div className="flex items-start gap-3 flex-wrap">
              <div className="min-w-0">
                <div className={LABEL} style={{ color: t.ink3 }}>The road ahead</div>
                <div className="font-display text-lg font-semibold tracking-tight" style={{ color: t.ink }}>
                  On track for {usd(outlook.byMaturity, 0)} of total return
                </div>
              </div>
              <div className="ml-auto grid grid-cols-3 gap-3 text-right">
                {[["Each month", usd(expectedMonthly, 0), "at your agreed rate"],
                  ["By", monLabel(outlook.horizon), "your last maturity"],
                  ["Still to come", usd(Math.max(0, outlook.byMaturity - (d.totals.accrued || 0)), 0), `over ${outlook.months} month${outlook.months === 1 ? "" : "s"}`],
                ].map(([k, v, sub]) => (
                  <div key={k}>
                    <div className="text-[10.5px] uppercase tracking-wider" style={{ color: t.ink3 }}>{k}</div>
                    <div className="font-display text-base font-semibold" style={{ color: t.ink }}>{v}</div>
                    <div className="text-[10.5px]" style={{ color: t.ink3 }}>{sub}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-3">
              <OutlookChart rows={outlook.rows} t={t} accent={accent} tid="statement-outlook-chart" />
            </div>
            <div className="flex items-center gap-4 mt-2 text-[11.5px] flex-wrap" style={{ color: t.ink3 }}>
              <span className="inline-flex items-center gap-1.5">
                <span className="w-4 h-0.5 rounded" style={{ background: PAID }} /> earned so far
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="w-4 h-0.5 rounded" style={{ background: accent, opacity: 0.85 }} /> projected at your agreed rate
              </span>
              <span>An estimate, not a promise — the dotted line assumes every month pays as agreed.</span>
            </div>
          </Bare>
        )}

        {commitments.map((c) => <CommitmentCard key={c.reference} c={c} t={t} accent={accent} />)}

        <PaymentsCard payments={d.payments} t={t} accent={accent} />

        <AdviserCard b={b} t={t} accent={accent} />

        <p className="text-[12px]" style={{ color: t.ink3 }}>{d.note}</p>
        {(b.contact_email || b.contact_phone) && (
          <p className="text-[12px]" style={{ color: t.ink3 }} data-testid="statement-contact">
            Questions? {b.contact_email}{b.contact_email && b.contact_phone ? " · " : ""}{b.contact_phone}
          </p>
        )}
      </div>
    </div>
  );
};

export const StatementMissing = ({ title, body, testid }) => (
  <div className="min-h-screen flex items-center justify-center p-6" data-testid={testid}>
    <div className="glass-strong p-10 text-center max-w-md">
      <Building2 size={26} className="mx-auto mb-3 text-ink-3" />
      <div className="font-display text-xl font-semibold">{title}</div>
      <p className="text-sm text-ink-2 mt-2">{body}</p>
    </div>
  </div>
);
